"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

const KENYA_COUNTIES = [
"Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet",
"Embu", "Garissa", "Homa Bay", "Isiolo", "Kajiado",
"Kakamega", "Kericho", "Kiambu", "Kilifi", "Kirinyaga",
"Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia",
"Lamu", "Machakos", "Makueni", "Mandera", "Marsabit",
"Meru", "Migori", "Mombasa", "Murang\'a", "Nairobi",
"Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua",
"Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River",
"Tharaka-Nithi", "Trans Nzoia", "Turkana", "Uasin Gishu",
"Vihiga", "Wajir", "West Pokot"];


const CATEGORIES = [
"Wildlife", "Cultural Heritage", "Beach Tourism", "Adventure Tourism",
"Conference Tourism", "Eco-Tourism", "Community-Based Tourism",
"Religious Tourism", "Investment Opportunity", "Sports Tourism"];


const SEASONS = ["January – March", "April – June", "July – September", "October – December", "Year-Round"];
const TARGET_MARKETS = ["Domestic Tourists", "East African Tourists", "International Tourists", "Business Travelers", "Adventure Seekers", "Luxury Travelers", "Budget Travelers", "All Markets"];
const AIRPORTS = [
"Jomo Kenyatta International (NBO)", "Moi International (MBA)", "Wilson Airport (WIL)",
"Kisumu International (KIS)", "Eldoret International (EDL)", "Ukunda Airstrip",
"Malindi Airport", "Other / Charter Airstrip"];


const STEPS = [
{ id: 1, label: "Basic Info", icon: "InformationCircleIcon" },
{ id: 2, label: "Location", icon: "MapPinIcon" },
{ id: 3, label: "Description", icon: "DocumentTextIcon" },
{ id: 4, label: "Media", icon: "PhotoIcon" },
{ id: 5, label: "Compliance", icon: "ShieldCheckIcon" }];


interface FormState {
  county: string;
  productName: string;
  category: string;
  physicalAddress: string;
  gpsLat: string;
  gpsLng: string;
  nearestAirport: string;
  accessibility: string;
  shortSummary: string;
  fullDescription: string;
  uniqueSellingPoints: string;
  bestSeason: string;
  targetMarket: string;
  virtualTourLink: string;
  ownershipDeclared: boolean;
  mediaRightsConfirmed: boolean;
  termsAccepted: boolean;
}

export default function ProductSubmissionClient() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const [form, setForm] = useState<FormState>({
    county: "",
    productName: "",
    category: "",
    physicalAddress: "",
    gpsLat: "",
    gpsLng: "",
    nearestAirport: "",
    accessibility: "",
    shortSummary: "",
    fullDescription: "",
    uniqueSellingPoints: "",
    bestSeason: "",
    targetMarket: "",
    virtualTourLink: "",
    ownershipDeclared: false,
    mediaRightsConfirmed: false,
    termsAccepted: false
  });

  const update = (field: keyof FormState, value: string | boolean) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: undefined }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (step === 1) {
      if (!form.county) newErrors.county = "Select your county";
      if (!form.productName) newErrors.productName = "Product name is required";
      if (!form.category) newErrors.category = "Select a category";
    }
    if (step === 2) {
      if (!form.physicalAddress) newErrors.physicalAddress = "Physical address is required";
      if (!form.nearestAirport) newErrors.nearestAirport = "Select nearest airport";
    }
    if (step === 3) {
      if (!form.shortSummary || form.shortSummary.length < 20) newErrors.shortSummary = "Minimum 20 characters required";
      if (!form.fullDescription || form.fullDescription.length < 50) newErrors.fullDescription = "Minimum 50 characters required";
      if (!form.bestSeason) newErrors.bestSeason = "Select best visiting season";
      if (!form.targetMarket) newErrors.targetMarket = "Select target market";
    }
    if (step === 5) {
      if (!form.ownershipDeclared) newErrors.ownershipDeclared = "You must declare ownership";
      if (!form.mediaRightsConfirmed) newErrors.mediaRightsConfirmed = "You must confirm media rights";
      if (!form.termsAccepted) newErrors.termsAccepted = "You must accept the terms";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const next = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep < 5) setCurrentStep((s) => s + 1);
  };

  const prev = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = () => {
    if (!validateStep(5)) return;
    const tn = `KTP-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000 + 1000)}`;
    setTrackingNumber(tn);
    setSubmitted(true);
  };

  const mockImageUpload = () => {
    const sampleImages = [
    "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=400&q=75",
    "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=400&q=75",
    "https://images.unsplash.com/photo-1559827291-72ee739d0d9a?w=400&q=75",
    "https://images.unsplash.com/photo-1605540436563-5bca919ae766?w=400&q=75",
    "https://images.unsplash.com/photo-1448375240586-882707db888b?w=400&q=75"];

    if (uploadedImages.length < 5) {
      const nextImg = sampleImages[uploadedImages.length];
      setUploadedImages((p) => [...p, nextImg]);
      if (!heroImage) setHeroImage(nextImg);
    }
  };

  const labelClass = "block text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed";
  const inputClass = (field: keyof FormState) =>
  `w-full bg-white border-2 rounded-xl px-4 py-3.5 text-[0.9375rem] text-kenya-black placeholder:text-text-muted/45 form-input transition-all ${
  errors[field] ? "border-kenya-red/50 bg-kenya-red/[0.02]" : "border-kenya-black/12 focus:border-kenya-green"}`;


  if (submitted) {
    return (
      <div className="text-center py-16 space-y-8">
        <div className="relative w-24 h-24 mx-auto">
          <div className="w-24 h-24 rounded-full bg-kenya-green/12 flex items-center justify-center">
            <Icon name="CheckBadgeIcon" size={50} className="text-kenya-green" />
          </div>
          <div className="absolute -top-1 -right-1 w-9 h-9 rounded-full bg-kenya-red flex items-center justify-center">
            <Icon name="StarIcon" size={15} className="text-white" variant="solid" />
          </div>
        </div>
        <div>
          <h2 className="text-4xl font-display font-bold text-kenya-black mb-3">Submission Received!</h2>
          <p className="text-text-muted text-lg max-w-md mx-auto">
            Your tourism product has been successfully submitted to the Ministry of Tourism & Wildlife for review.
          </p>
        </div>
        <div className="max-w-md mx-auto bg-kenya-black text-white rounded-xl2 p-8 space-y-4 text-left">
          <div className="flex items-center justify-between">
            <span className="text-white/50 text-[0.6875rem] font-bold uppercase tracking-widest font-condensed">Tracking Number</span>
            <span className="font-mono font-bold text-kenya-green text-xl">{trackingNumber}</span>
          </div>
          <div className="h-px bg-white/12" />
          <div className="grid grid-cols-2 gap-4">
            {[
            { label: "Product", value: form.productName || "Your Product" },
            { label: "Status", value: "Submitted" },
            { label: "Category", value: form.category },
            { label: "County", value: form.county }].
            map((item) =>
            <div key={item.label}>
                <div className="text-white/45 text-[0.6875rem] uppercase tracking-widest mb-1 font-condensed">{item.label}</div>
                {item.label === "Status" ?
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-300 text-[0.875rem] font-bold">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 pulse-dot" />
                    {item.value}
                  </div> :

              <div className="text-[0.9375rem] font-medium">{item.value}</div>
              }
              </div>
            )}
          </div>
          <div className="h-px bg-white/12" />
          <p className="text-[0.875rem] text-white/50 leading-relaxed">
            Confirmation sent to your registered email. Review takes 7–14 business days. You will receive status updates via email and SMS.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 max-w-lg mx-auto text-left">
          {[
          { icon: "ClockIcon", color: "text-amber-500", title: "Under Review", desc: "Ministry experts review your submission within 7–14 business days." },
          { icon: "EnvelopeIcon", color: "text-kenya-green", title: "Email Updates", desc: "Status updates sent at each stage of the review process." },
          { icon: "GlobeAltIcon", color: "text-kenya-red", title: "Go Global", desc: "Approved products featured across national and international channels." }].
          map((item) =>
          <div key={item.title} className="bg-white border-2 border-kenya-black/10 rounded-xl p-4 space-y-2">
              <Icon name={item.icon as Parameters<typeof Icon>[0]["name"]} size={22} className={item.color} />
              <div className="text-[0.9375rem] font-bold text-kenya-black">{item.title}</div>
              <div className="text-[0.875rem] text-text-muted leading-relaxed">{item.desc}</div>
            </div>
          )}
        </div>
        <button
          onClick={() => {
            setSubmitted(false);
            setCurrentStep(1);
            setForm({
              county: "", productName: "", category: "", physicalAddress: "",
              gpsLat: "", gpsLng: "", nearestAirport: "", accessibility: "",
              shortSummary: "", fullDescription: "", uniqueSellingPoints: "",
              bestSeason: "", targetMarket: "", virtualTourLink: "",
              ownershipDeclared: false, mediaRightsConfirmed: false, termsAccepted: false
            });
            setUploadedImages([]);
            setHeroImage(null);
          }}
          className="inline-flex items-center gap-2 border-2 border-kenya-black/12 text-text-muted px-6 py-3.5 rounded-xl text-[0.9375rem] font-bold hover:border-kenya-black/25 hover:text-kenya-black transition-all">
          
          <Icon name="PlusIcon" size={17} />
          Submit Another Product
        </button>
      </div>);

  }

  return (
    <div className="space-y-8">
      {/* Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[0.8125rem] font-bold text-text-muted uppercase tracking-widest font-condensed">Step {currentStep} of {STEPS.length}</span>
          <span className="text-[0.8125rem] font-bold text-kenya-green">{Math.round(currentStep / STEPS.length * 100)}% Complete</span>
        </div>
        <div className="h-2 bg-kenya-black/8 rounded-full overflow-hidden">
          <div className="progress-fill h-full rounded-full" style={{ width: `${currentStep / STEPS.length * 100}%` }} />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {STEPS.map((s) =>
          <button
            key={s.id}
            onClick={() => s.id < currentStep && setCurrentStep(s.id)}
            disabled={s.id > currentStep}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[0.6875rem] font-bold uppercase tracking-[0.15em] whitespace-nowrap transition-all font-condensed ${
            s.id === currentStep ?
            "bg-kenya-red text-white" :
            s.id < currentStep ?
            "bg-kenya-green/12 text-kenya-green cursor-pointer hover:bg-kenya-green/22" : "bg-kenya-black/6 text-text-muted cursor-not-allowed"}`
            }>
            
              {s.id < currentStep ?
            <Icon name="CheckIcon" size={13} /> :
            <Icon name={s.icon as Parameters<typeof Icon>[0]["name"]} size={13} />
            }
              {s.label}
            </button>
          )}
        </div>
      </div>

      {/* ── STEP 1: Basic Info ── */}
      {currentStep === 1 &&
      <div className="space-y-6">
          <div>
            <h2 className="text-3xl font-display font-bold text-kenya-black mb-2">Basic Information</h2>
            <p className="text-text-muted text-[0.9375rem]">Tell us about your tourism product.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className={labelClass}>
                County <span className="text-kenya-red">*</span>
              </label>
              <select value={form.county} onChange={(e) => update("county", e.target.value)} className={`${inputClass("county")} appearance-none`}>
                <option value="">Select county...</option>
                {KENYA_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.county && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.county}</p>}
            </div>
            <div className="space-y-2">
              <label className={labelClass}>
                Category <span className="text-kenya-red">*</span>
              </label>
              <select value={form.category} onChange={(e) => update("category", e.target.value)} className={`${inputClass("category")} appearance-none`}>
                <option value="">Select category...</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.category && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.category}</p>}
            </div>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>
              Product / Attraction Name <span className="text-kenya-red">*</span>
            </label>
            <input
            type="text"
            value={form.productName}
            onChange={(e) => update("productName", e.target.value)}
            placeholder="e.g. Rusinga Island Cultural Heritage Tour"
            className={inputClass("productName")} />
          
            {errors.productName && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.productName}</p>}
          </div>
          {form.category &&
        <div className="bg-kenya-green/8 border-2 border-kenya-green/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Icon name="LightBulbIcon" size={17} className="text-kenya-green mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[0.875rem] font-bold text-kenya-green mb-1">Examples of {form.category} in Kenya:</p>
                  <p className="text-[0.875rem] text-text-muted">
                    {form.category === "Wildlife" && "Maasai Mara Game Reserve, Amboseli National Park, Tsavo East & West"}
                    {form.category === "Cultural Heritage" && "Fort Jesus, Gede Ruins, Lamu Old Town, Thimlich Ohinga"}
                    {form.category === "Beach Tourism" && "Diani Beach, Watamu Marine Park, Malindi Beach, Lamu Archipelago"}
                    {form.category === "Adventure Tourism" && "Mount Kenya Trekking, Aberdare Canopy Walk, Tana River Rafting"}
                    {form.category === "Eco-Tourism" && "Kakamega Forest, Arabuko-Sokoke, Shimba Hills, Karura Forest"}
                    {form.category === "Community-Based Tourism" && "Il Ngwesi Eco-Lodge, Basecamp Masai Mara, Porini Camps"}
                    {form.category === "Conference Tourism" && "KICC Nairobi, Safari Park Hotel, Great Rift Valley Lodge"}
                    {form.category === "Religious Tourism" && "Basilica of the Holy Family, Jamia Mosque, Hindu Temples Mombasa"}
                    {form.category === "Investment Opportunity" && "New lodge developments, marina projects, eco-resort opportunities"}
                    {form.category === "Sports Tourism" && "Safari Rally Kenya, Rhino Charge, Maralal Camel Derby, Lewa Marathon"}
                  </p>
                </div>
              </div>
            </div>
        }
        </div>
      }

      {/* ── STEP 2: Location ── */}
      {currentStep === 2 &&
      <div className="space-y-6">
          <div>
            <h2 className="text-3xl font-display font-bold text-kenya-black mb-2">Location Details</h2>
            <p className="text-text-muted text-[0.9375rem]">Help visitors and marketing teams find your product.</p>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>
              Physical Address <span className="text-kenya-red">*</span>
            </label>
            <input
            type="text"
            value={form.physicalAddress}
            onChange={(e) => update("physicalAddress", e.target.value)}
            placeholder="e.g. Rusinga Island, Homa Bay County, Kenya"
            className={inputClass("physicalAddress")} />
          
            {errors.physicalAddress && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.physicalAddress}</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className={labelClass}>GPS Latitude</label>
              <input type="text" value={form.gpsLat} onChange={(e) => update("gpsLat", e.target.value)} placeholder="-0.3976" className={inputClass("gpsLat")} />
            </div>
            <div className="space-y-2">
              <label className={labelClass}>GPS Longitude</label>
              <input type="text" value={form.gpsLng} onChange={(e) => update("gpsLng", e.target.value)} placeholder="34.1836" className={inputClass("gpsLng")} />
            </div>
          </div>
          {/* Mock map */}
          <div className="space-y-2">
            <label className={labelClass}>Map Location Preview</label>
            <div className="h-48 bg-warm-surface rounded-xl border-2 border-kenya-black/10 overflow-hidden relative">
              <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1f732c9fa-1767299913639.png"
              alt="Kenya terrain map showing county boundaries and geographical regions"
              fill
              className="object-cover opacity-30"
              sizes="100vw" />
            
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-kenya-red/90 flex items-center justify-center mx-auto animate-bounce">
                    <Icon name="MapPinIcon" size={20} className="text-white" variant="solid" />
                  </div>
                  <p className="text-[0.8125rem] font-bold text-kenya-black/65 bg-white/85 px-3 py-1 rounded-full">
                    {form.gpsLat && form.gpsLng ? `${form.gpsLat}, ${form.gpsLng}` : "Enter GPS coordinates to pin location"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>
              Nearest Airport <span className="text-kenya-red">*</span>
            </label>
            <select value={form.nearestAirport} onChange={(e) => update("nearestAirport", e.target.value)} className={`${inputClass("nearestAirport")} appearance-none`}>
              <option value="">Select nearest airport...</option>
              {AIRPORTS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            {errors.nearestAirport && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.nearestAirport}</p>}
          </div>
          <div className="space-y-2">
            <label className={labelClass}>Accessibility Description</label>
            <textarea
            rows={3}
            value={form.accessibility}
            onChange={(e) => update("accessibility", e.target.value)}
            placeholder="Describe road conditions, transport options, distance from nearest town, special requirements..."
            className={`${inputClass("accessibility")} resize-none`} />
          
          </div>
        </div>
      }

      {/* ── STEP 3: Description ── */}
      {currentStep === 3 &&
      <div className="space-y-6">
          <div>
            <h2 className="text-3xl font-display font-bold text-kenya-black mb-2">Product Description</h2>
            <p className="text-text-muted text-[0.9375rem]">Compelling descriptions attract more visitors and better marketing placement.</p>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>
              Short Summary <span className="text-kenya-red">*</span>
              <span className="normal-case ml-2 text-text-muted/60">({form.shortSummary.length}/900 chars)</span>
            </label>
            <textarea
            rows={3}
            value={form.shortSummary}
            onChange={(e) => update("shortSummary", e.target.value)}
            placeholder="A concise, compelling overview of your tourism product for marketing materials and listings..."
            className={`${inputClass("shortSummary")} resize-none`}
            maxLength={900} />
          
            {errors.shortSummary && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.shortSummary}</p>}
          </div>
          <div className="space-y-2">
            <label className={labelClass}>
              Full Description <span className="text-kenya-red">*</span>
              <span className="normal-case ml-2 text-text-muted/60">({form.fullDescription.length}/3000 chars)</span>
            </label>
            <textarea
            rows={7}
            value={form.fullDescription}
            onChange={(e) => update("fullDescription", e.target.value)}
            placeholder="Provide a detailed description — its history, unique features, visitor experience, facilities, and what makes it special..."
            className={`${inputClass("fullDescription")} resize-none`}
            maxLength={3000} />
          
            {errors.fullDescription && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.fullDescription}</p>}
          </div>
          <div className="space-y-2">
            <label className={labelClass}>Unique Selling Points</label>
            <textarea
            rows={3}
            value={form.uniqueSellingPoints}
            onChange={(e) => update("uniqueSellingPoints", e.target.value)}
            placeholder="List 3–5 unique aspects that distinguish your product (one per line)..."
            className={`${inputClass("uniqueSellingPoints")} resize-none`} />
          
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className={labelClass}>
                Best Visiting Season <span className="text-kenya-red">*</span>
              </label>
              <select value={form.bestSeason} onChange={(e) => update("bestSeason", e.target.value)} className={`${inputClass("bestSeason")} appearance-none`}>
                <option value="">Select season...</option>
                {SEASONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.bestSeason && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.bestSeason}</p>}
            </div>
            <div className="space-y-2">
              <label className={labelClass}>
                Target Market <span className="text-kenya-red">*</span>
              </label>
              <select value={form.targetMarket} onChange={(e) => update("targetMarket", e.target.value)} className={`${inputClass("targetMarket")} appearance-none`}>
                <option value="">Select target market...</option>
                {TARGET_MARKETS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              {errors.targetMarket && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.targetMarket}</p>}
            </div>
          </div>
        </div>
      }

      {/* ── STEP 4: Media ── */}
      {currentStep === 4 &&
      <div className="space-y-6">
          <div>
            <h2 className="text-3xl font-display font-bold text-kenya-black mb-2">Media Upload</h2>
            <p className="text-text-muted text-[0.9375rem]">High-quality visuals are essential for effective marketing placement.</p>
          </div>
          <div className="bg-kenya-black/4 border-2 border-kenya-black/10 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="InformationCircleIcon" size={17} className="text-kenya-black/55" />
              <span className="text-[0.8125rem] font-bold text-kenya-black uppercase tracking-widest font-condensed">Image Requirements</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 text-[0.875rem] text-text-muted">
              {[
            "Minimum 5 images required",
            "Minimum resolution: 1920×1080px",
            "Maximum file size: 10MB per image",
            "Accepted formats: JPG, PNG, WebP"].
            map((req) =>
            <div key={req} className="flex items-center gap-2">
                  <Icon name="CheckIcon" size={13} className="text-kenya-green flex-shrink-0" />
                  {req}
                </div>
            )}
            </div>
          </div>
          <div>
            <label className={`${labelClass} mb-2 block`}>
              Product Images <span className="text-kenya-red">*</span>
              <span className="normal-case ml-2 text-text-muted/60">({uploadedImages.length}/5 minimum)</span>
            </label>
            <button
            type="button"
            onClick={mockImageUpload}
            disabled={uploadedImages.length >= 8}
            className="w-full upload-zone rounded-xl p-8 text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-xl bg-kenya-green/10 flex items-center justify-center mx-auto">
                  <Icon name="PhotoIcon" size={26} className="text-kenya-green" />
                </div>
                <div>
                  <p className="text-[0.9375rem] font-bold text-kenya-black">Click to upload images</p>
                  <p className="text-[0.875rem] text-text-muted mt-1">
                    {uploadedImages.length >= 8 ? "Maximum 8 images uploaded" : "Demo: click to add sample Kenyan tourism images"}
                  </p>
                </div>
              </div>
            </button>
          </div>
          {uploadedImages.length > 0 &&
        <div className="space-y-3">
              <label className={labelClass}>
                Uploaded Images — Click image to set as Hero
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {uploadedImages.map((img, idx) =>
            <div
              key={`img-${idx}`}
              onClick={() => setHeroImage(img)}
              className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
              heroImage === img ? "border-kenya-red" : "border-transparent hover:border-kenya-black/25"}`
              }>
              
                    <AppImage
                src={img}
                alt={`Uploaded tourism product image number ${idx + 1}`}
                fill
                className="object-cover"
                sizes="150px" />
              
                    {heroImage === img &&
              <div className="absolute inset-0 bg-kenya-red/20 flex items-center justify-center">
                        <span className="bg-kenya-red text-white text-[0.6875rem] font-bold px-2 py-1 rounded uppercase tracking-wider">Hero</span>
                      </div>
              }
                    <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const newImgs = uploadedImages.filter((_, i) => i !== idx);
                  setUploadedImages(newImgs);
                  if (heroImage === img) setHeroImage(newImgs[0] || null);
                }}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-kenya-black/65 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                
                      <Icon name="XMarkIcon" size={10} className="text-white" />
                    </button>
                  </div>
            )}
              </div>
            </div>
        }
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className={labelClass}>Video Upload (Optional)</label>
              <div className="upload-zone rounded-xl p-5 text-center cursor-pointer">
                <Icon name="VideoCameraIcon" size={22} className="text-text-muted mx-auto mb-2" />
                <p className="text-[0.875rem] text-text-muted">Upload promotional video (MP4, max 200MB)</p>
              </div>
            </div>
            <div className="space-y-2">
              <label className={labelClass}>Brochure PDF (Optional)</label>
              <div className="upload-zone rounded-xl p-5 text-center cursor-pointer">
                <Icon name="DocumentArrowUpIcon" size={22} className="text-text-muted mx-auto mb-2" />
                <p className="text-[0.875rem] text-text-muted">Upload existing brochure (PDF, max 20MB)</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>Virtual Tour Link (Optional)</label>
            <input
            type="url"
            value={form.virtualTourLink}
            onChange={(e) => update("virtualTourLink", e.target.value)}
            placeholder="https://my360tour.com/my-product"
            className={inputClass("virtualTourLink")} />
          
          </div>
        </div>
      }

      {/* ── STEP 5: Compliance ── */}
      {currentStep === 5 &&
      <div className="space-y-6">
          <div>
            <h2 className="text-3xl font-display font-bold text-kenya-black mb-2">Compliance & Declaration</h2>
            <p className="text-text-muted text-[0.9375rem]">Please review and confirm the following declarations before submitting.</p>
          </div>

          {/* Summary card */}
          <div className="bg-warm-surface rounded-xl2 border-2 border-kenya-black/8 p-6 space-y-4">
            <h3 className="text-[0.875rem] font-bold text-kenya-black uppercase tracking-widest font-condensed">Submission Summary</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
            { label: "Product Name", value: form.productName || "—" },
            { label: "Category", value: form.category || "—" },
            { label: "County", value: form.county || "—" },
            { label: "Images", value: `${uploadedImages.length} uploaded` },
            { label: "Season", value: form.bestSeason || "—" },
            { label: "Target Market", value: form.targetMarket || "—" }].
            map((item) =>
            <div key={item.label}>
                  <div className="text-[0.6875rem] font-bold uppercase tracking-widest text-text-muted font-condensed">{item.label}</div>
                  <div className="text-[0.9375rem] font-semibold text-kenya-black">{item.value}</div>
                </div>
            )}
            </div>
          </div>

          {/* Declarations */}
          <div className="space-y-4">
            {[
          {
            field: "ownershipDeclared" as keyof FormState,
            title: "Ownership Declaration",
            description: "I confirm that I am the legitimate owner or authorized representative of this tourism product and have the legal right to submit it for national marketing consideration by the Ministry of Tourism & Wildlife."
          },
          {
            field: "mediaRightsConfirmed" as keyof FormState,
            title: "Media Rights Confirmation",
            description: "I confirm that all uploaded images, videos, and documents are original or that I hold the rights to use them. I grant the Ministry of Tourism & Wildlife the right to use these materials for national and international marketing purposes."
          },
          {
            field: "termsAccepted" as keyof FormState,
            title: "Terms & Conditions",
            description: "I have read and agree to the County Product Portal Terms of Service, the Ministry's Data Protection Policy, and the Tourism Products Submission Guidelines (2026 Edition)."
          }].
          map((decl) =>
          <label
            key={decl.field}
            className={`flex items-start gap-4 cursor-pointer group p-5 rounded-xl border-2 transition-all ${
            errors[decl.field] ?
            "border-kenya-red/35 bg-kenya-red/[0.02]" :
            form[decl.field] ?
            "border-kenya-green/25 bg-kenya-green/[0.02]" :
            "border-kenya-black/10 hover:border-kenya-green/25"}`
            }>
            
                <div className="mt-0.5 flex-shrink-0">
                  <input
                type="checkbox"
                checked={form[decl.field] as boolean}
                onChange={(e) => update(decl.field, e.target.checked)}
                className="sr-only" />
              
                  <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                form[decl.field] ?
                "bg-kenya-green border-kenya-green" :
                errors[decl.field] ?
                "border-kenya-red" : "border-kenya-black/25 group-hover:border-kenya-green/50"}`
                }>
                
                    {form[decl.field] && <Icon name="CheckIcon" size={12} className="text-white" />}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-[0.9375rem] font-bold text-kenya-black">{decl.title}</p>
                  <p className="text-[0.875rem] text-text-muted leading-relaxed">{decl.description}</p>
                  {errors[decl.field] && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors[decl.field]}</p>}
                </div>
              </label>
          )}
          </div>

          {/* CAPTCHA placeholder */}
          <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-kenya-black/10 bg-warm-surface">
            <div className="w-6 h-6 rounded border-2 border-kenya-black/25 flex items-center justify-center flex-shrink-0">
              <Icon name="ShieldCheckIcon" size={14} className="text-kenya-green" />
            </div>
            <div>
              <p className="text-[0.875rem] font-bold text-kenya-black">Spam Protection</p>
              <p className="text-[0.8125rem] text-text-muted">This form is protected against automated submissions.</p>
            </div>
            <div className="ml-auto">
              <Icon name="LockClosedIcon" size={19} className="text-text-muted" />
            </div>
          </div>
        </div>
      }

      {/* Navigation buttons */}
      <div className="flex gap-3 pt-4 border-t-2 border-kenya-black/8">
        {currentStep > 1 &&
        <button
          type="button"
          onClick={prev}
          className="flex items-center gap-2 px-6 py-4 border-2 border-kenya-black/12 rounded-xl text-[0.9375rem] font-bold text-text-muted hover:border-kenya-black/25 hover:text-kenya-black transition-all">
          
            <Icon name="ArrowLeftIcon" size={17} />
            Back
          </button>
        }
        {currentStep < 5 ?
        <button
          type="button"
          onClick={next}
          className="flex-1 flex items-center justify-center gap-2 py-4 bg-kenya-red text-white rounded-xl text-[0.9375rem] font-bold tracking-[0.05em] hover:bg-kenya-red/90 transition-all shadow-kenya">
          
            Continue
            <Icon name="ArrowRightIcon" size={17} />
          </button> :

        <button
          type="button"
          onClick={handleSubmit}
          className="flex-1 flex items-center justify-center gap-2 py-4 bg-kenya-green text-white rounded-xl text-[0.9375rem] font-bold tracking-[0.05em] hover:bg-kenya-green/90 transition-all shadow-kenya-green">
          
            <Icon name="PaperAirplaneIcon" size={17} />
            Submit to Ministry
          </button>
        }
      </div>
    </div>);

}